"use client"

import { PriceWrapper } from "@/app/price"
import type { Currency } from "@anaconda/core/price/types"
import { cn } from "@rarible_int/anaconda_ui_components"

const AXIS_LINE_HEIGHT = 1

export function DateLabel({
  position,
  value,
  className,
  approximateWidth,
}: {
  position: { x: number; y: number }
  value: string
  className?: string
  /**
   * Used to calculate the position of the date label to avoid overflow of the chart
   * Approximate width is used instead of exact width calculation to avoid extra computations and complexity
   *
   * In case styles or format of the date label changes, this value should be updated
   */
  approximateWidth: number
}) {
  return (
    <Label
      position={{
        x: Math.max(position.x, approximateWidth / 2),
        y: position.y + AXIS_LINE_HEIGHT,
      }}
      value={value}
      className={cn("-translate-x-1/2 bg-background p-1 text-white", className)}
    />
  )
}

export function FloorLabel({
  value,
  currency,
  position,
  className,
}: {
  value: number
  currency: Currency
  position: { x: number; y: number }
  className?: string
}) {
  return (
    <PriceLabel
      position={position}
      value={value}
      currency={currency}
      className={cn("bg-brand/80", className)}
    />
  )
}

export function PriceLabel({
  position,
  value,
  className,
  currency,
}: {
  position: { x: number; y: number }
  value: number
  className?: string
  currency: Currency
}) {
  return (
    <Label
      position={position}
      value={
        <PriceWrapper
          className="[&_span]:text-muted"
          amount={value.toString()}
          currency={currency}
        />
      }
      className={cn(
        "-translate-y-1/2 -translate-x-full bg-[rgba(255,255,255,0.60)] px-2 py-[2px] text-muted backdrop-blur-[10px]",
        className,
      )}
    />
  )
}

function Label({
  position,
  value,
  className,
}: {
  position: { x: number; y: number }
  value: React.ReactNode
  className?: string
}) {
  return (
    <foreignObject
      className="relative overflow-visible"
      x={position.x}
      y={position.y}
    >
      <div className={cn("flex w-fit text-nowrap", className)}>{value}</div>
    </foreignObject>
  )
}
